/** Everything the sign-in flow decides without a browser, HTTP or Angular. */

/** The route Google is registered to return to. The redirect URI is this plus the origin. */
export const CALLBACK_ROUTE = 'auth/callback';
export const CALLBACK_PATH = '/' + CALLBACK_ROUTE;

/** Where a sign-in lands when nothing was being attempted. */
export const DEFAULT_RETURN_URL = '/companies';

const AUTHORIZE_ENDPOINT = 'https://accounts.google.com/o/oauth2/v2/auth';

const AUTH_PREFIX = '/auth/';

/**
 * The two auth endpoints take no bearer token, and their 401s are the ordinary refusal path —
 * catching those as expiry turns a failed sign-in into a redirect loop.
 */
export function isAuthCall(url: string, apiBaseUrl: string): boolean {
  return url.startsWith(apiBaseUrl.replace(/\/+$/, '') + AUTH_PREFIX);
}

/** The one record parked in sessionStorage across the redirect out to Google. */
export interface ParkedSignIn {
  verifier: string;
  state: string;
  returnUrl: string | null;
}

export interface SignInStart {
  authorizeUrl: string;
  parked: ParkedSignIn;
}

export interface SignInConfig {
  /** The browser's own origin. Deriving the redirect URI from it cannot drift from the build. */
  origin: string;
  clientId: string;
  allowedDomain: string;
}

/** Discriminated so a caller cannot read a code off a refused callback. */
export type CallbackResult =
  | { kind: 'exchange'; code: string; verifier: string; returnUrl: string }
  | { kind: 'refused'; slug: string; message: string };

/** No `prompt`, so an already-signed-in recruiter passes straight through; `hd` is a hint only. */
export async function startSignIn(config: SignInConfig, returnUrl: string | null): Promise<SignInStart> {
  const verifier = randomUrlSafe(64);
  const state = randomUrlSafe(16);
  const url = new URL(AUTHORIZE_ENDPOINT);
  url.search = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: config.origin.replace(/\/+$/, '') + CALLBACK_PATH,
    response_type: 'code',
    scope: 'openid email profile',
    code_challenge: await s256(verifier),
    code_challenge_method: 'S256',
    state,
    hd: config.allowedDomain,
  }).toString();
  return { authorizeUrl: url.toString(), parked: { verifier, state, returnUrl } };
}

/** Refuses outright on anything unexpected — no partial trust, no attempt to exchange. */
export function resolveCallback(
  params: Record<string, string | undefined>,
  parked: ParkedSignIn | null,
  allowedDomain: string,
): CallbackResult {
  const refused = (slug: string): CallbackResult => ({ kind: 'refused', slug, message: refusalMessage(slug, allowedDomain) });
  if (params['error']) return refused(params['error']);
  if (!parked) return refused('no-parked-sign-in');
  if (!params['state']) return refused('state-missing');
  if (params['state'] !== parked.state) return refused('state-mismatch');
  if (!params['code']) return refused('no-code');
  return {
    kind: 'exchange',
    code: params['code'],
    verifier: parked.verifier,
    returnUrl: parked.returnUrl || DEFAULT_RETURN_URL,
  };
}

/** Vetta's own callback refusals, Google's error parameter, and the slugs Recruit Service names. */
const SIGN_IN_REFUSAL: Record<string, string> = {
  'no-parked-sign-in': 'This sign-in did not start in this tab. Start again from the sign-in page.',
  'state-missing': 'Google’s reply could not be verified. Start again from the sign-in page.',
  'state-mismatch': 'Google’s reply did not match this tab. Start again from the sign-in page.',
  'no-code': 'Google returned no sign-in code. Start again from the sign-in page.',
  access_denied: 'Sign-in was cancelled. Use the button to try again.',
  'invalid-request': 'Recruit Service could not read the sign-in request. Nothing is wrong with the account.',
  'google-auth-failed': 'Recruit Service could not verify the sign-in with Google. Nothing is wrong with the account.',
  'session-not-found': 'The sign-in session was not found. Sign in again.',
  'session-expired': 'The sign-in session expired before it finished. Sign in again.',
  'session-unusable': 'The sign-in session is no longer valid. Sign in again.',
  'not-an-employee': 'That Google account cannot sign in. Use your {domain} account.',
};

export const UNRECOGNISED_REFUSAL = 'Sign-in could not be completed. Start again from the sign-in page.';

export function refusalMessage(slug: string, allowedDomain: string): string {
  return (SIGN_IN_REFUSAL[slug] ?? UNRECOGNISED_REFUSAL).replace('{domain}', allowedDomain);
}

/** `exp` only, read for convenience; Recruit Service remains the authority. `now` is the harness seam. */
export function tokenIsUsable(token: string | null | undefined, now: number = Date.now()): boolean {
  const exp = claimsOf(token ?? '')?.['exp'];
  return typeof exp === 'number' && exp * 1000 > now;
}

/** JWT claims, or null for anything that will not parse. Never throws. */
export function claimsOf(token: string): Record<string, unknown> | null {
  const parts = token.split('.');
  if (parts.length !== 3 || parts.some((p) => !p)) return null;
  try {
    const json = decodeURIComponent(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    const claims: unknown = JSON.parse(json);
    if (!claims || typeof claims !== 'object' || Array.isArray(claims)) return null;
    return claims as Record<string, unknown>;
  } catch {
    return null;
  }
}

async function s256(verifier: string): Promise<string> {
  return base64Url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
}

function randomUrlSafe(bytes: number): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(bytes)));
}

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
